import React, { useEffect, useState } from "react";
import { DatePicker, Button } from "antd";
import type { RangePickerProps } from "antd/es/date-picker";
import dayjs from "dayjs";
import { ModalBase } from "@aspen/ui";
import { i18nCrossUtil, PUBLIC_PATHS } from "@aspen/libs";
import { useRouter } from "next/router";

const { RangePicker } = DatePicker;

export interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * 导出历史记录的状态枚举
 * 用于管理ModalExportHistory组件中导出操作的不同状态
 */
export enum ExportStatus {
  /** 初始状态 */
  INIT,
  /** 没有历史记录 */
  NO_RECORDS,
  /** 导出成功 */
  EXPORT_SUCCESS,
}

export interface Description {
  text: string;
  className?: string;
}

export interface ModalExportHistoryProps {
  open: boolean;
  onClose: () => void;
  onExport: (dateRange: DateRange) => Promise<ExportStatus>;
  descriptions?: Description[];
}

export const ModalExportHistory: React.FC<ModalExportHistoryProps> = ({
  open,
  onClose,
  onExport,
  descriptions = [],
}) => {
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [exportStatus, setExportStatus] = useState<ExportStatus>(ExportStatus.INIT);
  const intl = i18nCrossUtil.t();
  const router = useRouter();

  // 弹窗关闭时清空状态
  useEffect(() => {
    if (!open) {
      setExportStatus(ExportStatus.INIT);
      setDateRange(null);
    }
  }, [open]);

  const handleDateChange: RangePickerProps["onChange"] = dates => {
    if (dates && dates[0] && dates[1]) {
      const newDateRange = {
        startDate: dates[0].format("YYYY-MM-DD"),
        endDate: dates[1].format("YYYY-MM-DD"),
      };
      setDateRange(newDateRange);

      // 清空导出状态，因为时间范围已变化
      setExportStatus(ExportStatus.INIT);
    } else {
      setDateRange(null);
      // 清空导出状态
      setExportStatus(ExportStatus.INIT);
    }
  };

  const handleExport = async () => {
    if (!dateRange) return;

    setIsLoading(true);
    // 重置状态
    setExportStatus(ExportStatus.INIT);

    try {
      const status = await onExport(dateRange);
      setExportStatus(status);
    } catch (error) {
      setExportStatus(ExportStatus.INIT);
    } finally {
      setIsLoading(false);
    }
  };

  const isExportDisabled = !dateRange || isLoading;

  return (
    <ModalBase
      open={open}
      onCancel={onClose}
      title={intl["export.history.title"]}
      footer={null}
      width={480}
      maskClosable={false}
      keyboard={false}>
      <div className="font-common-regular text-[14px] leading-[22px] text-[rgba(255,255,255,0.7)] w-full">
        <p className="mb-8">
          {descriptions.map((description, index) => (
            <span key={index} className={description.className}>
              {description.text}
            </span>
          ))}
        </p>
      </div>

      <RangePicker
        className="w-full"
        onChange={handleDateChange}
        placeholder={[intl["export.history.start.date"], intl["export.history.end.date"]]}
        inputReadOnly
        allowClear={false}
        disabledDate={current => {
          if (!current) return false;
          // 禁用早于2024-01-01的日期
          const isBeforeMinDate = current.startOf("day") < dayjs("2024-01-01").startOf("day");
          // 禁用今天及以后的日期（今天不能选择）
          const isTodayOrAfter = current >= dayjs().startOf("day");
          return isBeforeMinDate || isTodayOrAfter;
        }}
        format="YYYY-MM-DD"
      />

      {/* 状态提示文字 */}
      {exportStatus === ExportStatus.NO_RECORDS && (
        <div className="font-common-regular text-[10px] leading-[12px] text-[#f10000] mt-2">
          <p>{intl["export.history.message.no.records"]}</p>
        </div>
      )}

      <div className="flex justify-end w-full mt-8">
        {/* 导出成功状态 - 显示查看文档按钮 */}
        {exportStatus === ExportStatus.EXPORT_SUCCESS && (
          <Button
            type="primary"
            onClick={() => {
              router.push({
                pathname: PUBLIC_PATHS.DOCUMENT_CENTER_PATH,
                query: {
                  type: "generatedHistory",
                },
              });
            }}
            className="w-full bg-[#f17e00] border-[#f17e00] hover:bg-[#e66a00] hover:border-[#e66a00] font-common-regular text-[14px] h-9 px-[29px] rounded-[2px]">
            {intl["export.history.button.view.document"]}
          </Button>
        )}

        {/* 无记录状态 - 显示置灰的导出按钮 */}
        {exportStatus === ExportStatus.NO_RECORDS && (
          <Button
            type="primary"
            disabled={true}
            className="w-full bg-[#383949] border-[#383949] text-[#737580] font-common-regular text-[14px] h-9 px-[29px] rounded-[2px]">
            {intl["export.history.button.export"]}
          </Button>
        )}

        {/* 默认状态 - 显示可点击的导出按钮 */}
        {exportStatus === ExportStatus.INIT && (
          <Button
            type="primary"
            loading={isLoading}
            disabled={isExportDisabled}
            onClick={() => {
              handleExport();
            }}
            className="w-full bg-[#f17e00] border-[#f17e00] hover:bg-[#e66a00] hover:border-[#e66a00] disabled:bg-[#383949] disabled:border-[#383949] disabled:text-[#737580] font-common-regular text-[14px] h-9 px-[29px] rounded-[2px]">
            {isLoading
              ? intl["export.history.button.exporting"]
              : intl["export.history.button.export"]}
          </Button>
        )}
      </div>
    </ModalBase>
  );
};

export default ModalExportHistory;
